import { Link } from 'react-router-dom'
import { ArrowLeft, Database, CheckCircle2, AlertTriangle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useGame } from '../context/GameContext'
import { loginPlayer, type PlayerData } from '../utils/api'

export default function DebugData() {
  const { progress } = useGame()
  const [player, setPlayer] = useState<PlayerData | null>(null)

  useEffect(() => {
    const pw = localStorage.getItem('hsk2-current-player')
    if (!pw) return
    loginPlayer(pw).then(setPlayer).catch(() => setPlayer(null))
  }, [])

  const checks = useMemo(() => {
    const localTotalAttempts = Object.values(progress.wordStats || {}).reduce((sum, s) => sum + (s.correct || 0) + (s.wrong || 0), 0)
    const localTotalCorrect = Object.values(progress.wordStats || {}).reduce((sum, s) => sum + (s.correct || 0), 0)
    const localTotalWrong = Object.values(progress.wordStats || {}).reduce((sum, s) => sum + (s.wrong || 0), 0)

    const remoteTotalCorrect = player?.totalCorrect || 0
    const remoteTotalQuestions = player?.totalQuestions || 0
    const remoteWordCorrect = (player?.wordsLearned || []).reduce((sum, w) => sum + (w.correctCount || 0), 0)

    return [
      {
        label: 'Remote totals consistency (correct <= questions)',
        ok: remoteTotalCorrect <= remoteTotalQuestions,
        detail: `${remoteTotalCorrect} / ${remoteTotalQuestions}`,
      },
      {
        label: 'Remote words aggregate vs player totals',
        ok: remoteWordCorrect <= remoteTotalCorrect,
        detail: `wordCorrect=${remoteWordCorrect}, playerCorrect=${remoteTotalCorrect}`,
      },
      {
        label: 'Local stats non-negative',
        ok: localTotalAttempts >= 0 && localTotalCorrect >= 0 && localTotalWrong >= 0,
        detail: `attempts=${localTotalAttempts}, correct=${localTotalCorrect}, wrong=${localTotalWrong}`,
      },
      {
        label: 'Remote category stats present',
        ok: (player?.categoryStats || []).length > 0,
        detail: `${(player?.categoryStats || []).length} categories`,
      },
    ]
  }, [player, progress.wordStats])

  return (
    <div className="page-shell bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 p-3 md:p-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/" className="bg-white/20 rounded-2xl p-2.5 md:p-3 active:scale-95 transition-transform">
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>
          <div className="flex items-center gap-2">
            <Database className="w-7 h-7 text-cyan-300" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Debug Data Consistency</h1>
          </div>
        </div>

        <div className="bg-white/10 rounded-2xl p-4 mb-4">
          <p className="text-white/70 text-sm">Use this to quickly validate save logic and data sync.</p>
        </div>

        <div className="space-y-3">
          {checks.map((c, idx) => (
            <div key={idx} className={`rounded-2xl p-3 border ${c.ok ? 'bg-green-500/20 border-green-400/40' : 'bg-red-500/20 border-red-400/40'}`}>
              <div className="flex items-center gap-2">
                {c.ok ? <CheckCircle2 className="w-5 h-5 text-green-300" /> : <AlertTriangle className="w-5 h-5 text-red-300" />}
                <p className="text-white font-semibold">{c.label}</p>
              </div>
              <p className="text-white/80 text-sm mt-1">{c.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
